import EV_PRESETS_DATA from './evSimulatorPresets.json';

// EV Powertrain Simulator: field metadata + client-side per-field validation.
// Field keys mirror backend/simulation/schemas.py's FullSimulationRequest exactly
// (VehicleParamsFullIn, BatteryParamsFullIn) — no renamed/invented request fields.
//
// Motor characteristics are NOT an input group here — they're a computed requirement
// (see MotorRequirementOut / §4 of the tool spec), driven entirely by these Vehicle
// Parameter fields plus Motor Safety Margin.

export const VEHICLE_FIELD_SPECS = [
  { key: 'top_speed_flat_kph', label: 'Top Speed on Flat Road', unit: 'km/h', min: 30, max: 400, strictlyPositive: true },
  { key: 'top_speed_hillclimb_kph', label: 'Top Speed on Hill Climb', unit: 'km/h', min: 5, max: 200, strictlyPositive: true },
  { key: 'road_slope_high_speed_pct', label: 'Road Slope for High Speed', unit: '%', min: 0, max: 15, strictlyPositive: false },
  { key: 'max_hill_climb_slope_pct', label: 'Max. Hill Climb Slope', unit: '%', min: 1, max: 60, strictlyPositive: true },
  { key: 'accel_time_s', label: 'Acceleration Time (0-100 kph)', unit: 's', min: 1, max: 60, strictlyPositive: true },
  { key: 'cd', label: 'Aerodynamic Drag Coefficient (Cd)', unit: 'Cd', min: 0.10, max: 1.00, strictlyPositive: true },
  { key: 'frontal_area_m2', label: 'Maximum Frontal Area', unit: 'm²', min: 0.50, max: 6.00, strictlyPositive: true },
  { key: 'mass_kg', label: 'Vehicle Mass', unit: 'kg', min: 200, max: 6000, strictlyPositive: true },
  { key: 'max_payload_kg', label: 'Maximum Payload', unit: 'kg', min: 0, max: 2000, strictlyPositive: false },
  { key: 'crr', label: 'Rolling Resistance Coefficient', unit: 'Crr', min: 0.003, max: 0.05, strictlyPositive: true },
  { key: 'wheel_radius_m', label: 'Tire Rolling Radius', unit: 'm', min: 0.15, max: 0.60, strictlyPositive: true },
  // Real single-speed EV drivelines run ~0.90-0.98; 0.80 already allows a very lossy design.
  { key: 'driveline_efficiency', label: 'Driveline Efficiency', unit: '0-1', min: 0.80, max: 0.99, strictlyPositive: true },
  { key: 'gear_ratio', label: 'Transmission Gear Ratio', unit: ':1', min: 1.0, max: 20.0, strictlyPositive: true },
  { key: 'tire_friction_coeff', label: 'Tire Friction Coefficient', unit: 'μ', min: 0.1, max: 2.0, strictlyPositive: true },
  { key: 'motor_safety_margin_pct', label: 'Motor Safety Margin', unit: '%', min: 0, max: 50, strictlyPositive: false },
];

export const BATTERY_FIELD_SPECS = [
  { key: 'pack_nominal_voltage_v', label: 'Battery Pack Nominal Voltage', unit: 'V', min: 12, max: 1000, strictlyPositive: true },
  { key: 'cell_nominal_voltage_v', label: 'Battery Cell Nominal Voltage', unit: 'V', min: 1.0, max: 5.0, strictlyPositive: true },
  { key: 'cell_capacity_ah', label: 'Battery Cell Rated Capacity', unit: 'Ah', min: 0.1, max: 500, strictlyPositive: true },
  { key: 'cell_c_rate', label: 'Battery Cell C-Rate', unit: 'C', min: 0.1, max: 20, strictlyPositive: true },
  { key: 'cell_mass_kg', label: 'Battery Cell Mass', unit: 'kg', min: 0.001, max: 10, strictlyPositive: true },
  { key: 'aux_power_w', label: 'Auxiliary Systems Power', unit: 'W', min: 0, max: 20000, strictlyPositive: false },
  { key: 'min_soc_pct', label: 'Minimum Battery SoC', unit: '%', min: 0, max: 100, strictlyPositive: false },
  { key: 'max_soc_pct', label: 'Maximum Battery SoC', unit: '%', min: 0, max: 100, strictlyPositive: false },
  { key: 'internal_resistance_ohm', label: 'Battery Internal Resistance (Average)', unit: 'Ω', min: 0, max: 5, strictlyPositive: false },
  { key: 'extra_strings', label: 'Additional Battery Strings', unit: 'count', min: 0, max: 500, strictlyPositive: false, integer: true },
  { key: 'vehicle_range_km', label: 'Vehicle Range', unit: 'km', min: 1, max: 3000, strictlyPositive: true },
];

// Vehicle presets: four real EVs whose published data was validated against this simulator
// on 2026-09-25 (see evSimulatorPresets.json — each entry carries a sourcing note naming its
// estimated fields). Selecting one loads every Vehicle + Battery field and the drive cycle it
// was certified on. Tata Nexon EV Max is first by product decision and is the page default.
export const EV_PRESETS = EV_PRESETS_DATA;
export const DEFAULT_PRESET_ID = EV_PRESETS[0].id;
export const CUSTOM_PRESET_ID = 'custom';

export function findPreset(id) {
  return EV_PRESETS.find((p) => p.id === id) || null;
}

// Non-numeric inputs carried in the same raw objects as the number fields (so a preset
// loads in one setState); VEHICLE_/BATTERY_FIELD_SPECS validation skips them.
export const DRIVE_TECHNOLOGY_OPTIONS = [
  { value: 'legacy', label: 'Legacy Si-IGBT (2010s design)' },
  { value: 'standard', label: 'Modern Si-IGBT' },
  { value: 'sic', label: 'SiC inverter (high efficiency)' },
];
export const CHEMISTRY_OPTIONS = [
  { value: 'NMC', label: 'NMC' },
  { value: 'LFP', label: 'LFP' },
];

export const DEFAULT_VEHICLE = { ...EV_PRESETS[0].vehicle };
export const DEFAULT_BATTERY = { ...EV_PRESETS[0].battery };

export const DRIVE_CYCLE_OPTIONS = [
  { value: 'wltc', label: 'WLTC', disabled: false },
  { value: 'midc', label: 'MIDC', disabled: false },
];

export const DEFAULT_DRIVE_CYCLE = EV_PRESETS[0].drive_cycle;

// Simulation Sampling Time (dt): the drive-cycle simulation's time step. Fixed 0.1-1.0s
// dial in 0.1s increments (not a free-form number) to match the backend's
// Literal-validated field. Defaults to 1.0s, the source cycles' own native spacing.
export const SAMPLING_TIME_OPTIONS_S = Array.from({ length: 10 }, (_, i) => Math.round((i + 1) * 10) / 100);
export const DEFAULT_SAMPLING_TIME_S = 1.0;

// How long the drive-cycle simulation runs — a range/endurance run, like a MATLAB-style
// range estimator: it loops the selected drive cycle and stops as soon as SOC reaches
// Minimum Battery SoC OR this many seconds elapse, whichever happens first.
// Left blank (optional: true — the field starts empty and Submit works without it): the
// backend loops until SOC reaches Minimum Battery SoC, up to an internal 172800s (48h)
// safety ceiling. After every run the field is updated to the actual elapsed simulation
// time the backend reports back, whether that matches what was typed (never depleted
// early) or was cut short by depletion — so re-submitting without touching it repeats the
// same duration. Battery Discharge / Battery Energy Consumption graphs reflect this
// duration; the Vehicle Graphs acceleration trace (plot 1) is intentionally independent
// of it.
export const SIMULATION_TIME_SPEC = {
  key: 'simulation_time_s', label: 'Simulation Time', unit: 's', min: 60, max: 172800,
  strictlyPositive: true, optional: true,
};

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return spec.optional ? null : 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.integer && !Number.isInteger(value)) {
    return 'Must be a whole number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && spec.min !== undefined && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && spec.max !== undefined && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}

// Cross-field rules, mirrored in backend/simulation/schemas.py (VehicleParamsFullIn /
// BatteryParamsFullIn model validators). Run only once every field involved passes its own
// per-field check; returns { fieldKey: message } for the field the user should fix.
export const ACCEL_TARGET_KPH = 100;
export const MIN_SOC_WINDOW_PCT = 20;

export function validateCrossFields(vehicle, battery) {
  const errors = {};
  if (vehicle.top_speed_flat_kph !== undefined && vehicle.top_speed_flat_kph <= ACCEL_TARGET_KPH) {
    errors.top_speed_flat_kph = 'Must be above 100 km/h (acceleration is specified as 0-100 km/h).';
  }
  if (vehicle.top_speed_hillclimb_kph !== undefined && vehicle.top_speed_flat_kph !== undefined
      && vehicle.top_speed_hillclimb_kph > vehicle.top_speed_flat_kph) {
    errors.top_speed_hillclimb_kph = 'Cannot exceed Top Speed on Flat Road.';
  }
  if (battery.min_soc_pct !== undefined && battery.max_soc_pct !== undefined
      && battery.max_soc_pct - battery.min_soc_pct < MIN_SOC_WINDOW_PCT) {
    errors.max_soc_pct = `Must be at least ${MIN_SOC_WINDOW_PCT} points above Minimum Battery SoC.`;
  }
  if (battery.cell_nominal_voltage_v !== undefined && battery.pack_nominal_voltage_v !== undefined
      && battery.cell_nominal_voltage_v > battery.pack_nominal_voltage_v) {
    errors.cell_nominal_voltage_v = 'Cannot exceed Battery Pack Nominal Voltage.';
  }
  return errors;
}

export function toRawStrings(defaults) {
  return Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]));
}
